import { t } from './i18n.js';
import { useEffect, useId, useState } from 'react';
import { Check, Users, X } from 'lucide-react';
import { api } from './api.js';
import { sourceLabel, type AddressSuggestion as Suggestion } from './RecipientField.js';
export const guestAddresses = (value: string) => [
  ...new Set(
    value
      .split(/[;,\n]/)
      .map((s) => s.trim().toLowerCase())
      .filter(Boolean),
  ),
];
const valid = (value: string) => /^[^\s@<>;,]+@[^\s@<>;,]+\.[^\s@<>;,]+$/.test(value);
export function GuestPicker({
  value,
  query,
  onChange,
  onQueryChange,
}: {
  value: string;
  query: string;
  onChange: (value: string) => void;
  onQueryChange: (query: string) => void;
}) {
  const id = useId(),
    [open, setOpen] = useState(false),
    [results, setResults] = useState<Suggestion[]>([]),
    [active, setActive] = useState(0),
    [loading, setLoading] = useState(false),
    [error, setError] = useState('');
  const addresses = guestAddresses(value),
    choices = results.filter((r) => !addresses.includes(r.email));
  useEffect(() => {
    if (!open) return;
    const abort = new AbortController();
    setLoading(true);
    setResults([]);
    setActive(0);
    setError('');
    const timer = setTimeout(() => {
      void api<Suggestion[]>('/calendar/guests?q=' + encodeURIComponent(query), {
        signal: abort.signal,
      })
        .then((rows) => {
          if (!abort.signal.aborted) setResults(rows);
        })
        .catch((e) => {
          if (!abort.signal.aborted) setError(e.message);
        })
        .finally(() => {
          if (!abort.signal.aborted) setLoading(false);
        });
    }, 180);
    return () => {
      clearTimeout(timer);
      abort.abort();
    };
  }, [query, open]);
  const add = (email: string) => {
    const next = guestAddresses(email);
    if (!next.length || next.some((s) => !valid(s))) {
      setError(t('Saisissez une adresse email valide ou choisissez une suggestion.'));
      return;
    }
    if (new Set([...addresses, ...next]).size > 50) {
      setError(t('Vous pouvez inviter 50 personnes au maximum.'));
      return;
    }
    onChange([...new Set([...addresses, ...next])].join(', '));
    onQueryChange('');
    setOpen(false);
    setError('');
    setActive(0);
  };
  return (
    <div className="guest-picker">
      <label htmlFor={id}>
        <Users size={18} /> {t('Ajouter des invités')}
      </label>
      <div className="guest-chips">
        {addresses.map((email) => (
          <span className="guest-chip" key={email}>
            <span className="guest-initial">{email[0].toUpperCase()}</span>
            <span>{email}</span>
            <button
              type="button"
              aria-label={`Retirer ${email}`}
              onClick={() => onChange(addresses.filter((a) => a !== email).join(', '))}
            >
              <X size={13} />
            </button>
          </span>
        ))}
      </div>
      <div className="guest-search">
        <input
          id={id}
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={open}
          aria-controls={id + '-results'}
          aria-activedescendant={open && choices[active] ? id + '-' + active : undefined}
          autoComplete="off"
          placeholder={t('Nom ou adresse email')}
          value={query}
          maxLength={120}
          onFocus={() => setOpen(true)}
          onBlur={() => setOpen(false)}
          onChange={(e) => {
            onQueryChange(e.target.value);
            setOpen(true);
          }}
          onPaste={(e) => {
            const text = e.clipboardData.getData('text');
            if (/[,;\n]/.test(text)) {
              e.preventDefault();
              add(text);
            }
          }}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
              e.preventDefault();
              setOpen(true);
              setActive((n) =>
                Math.max(0, Math.min(choices.length - 1, n + (e.key === 'ArrowDown' ? 1 : -1))),
              );
            } else if (e.key === 'Escape') {
              e.preventDefault();
              e.stopPropagation();
              setOpen(false);
            } else if (e.key === 'Enter') {
              e.preventDefault();
              if (open && choices[active]) add(choices[active].email);
              else if (query.trim()) add(query);
            } else if (e.key === ',' || e.key === ';') {
              e.preventDefault();
              add(query);
            }
          }}
        />
        {query && (
          <button
            type="button"
            aria-label={t('Ajouter cette adresse')}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => add(query)}
          >
            <Check size={18} />
          </button>
        )}
        {open && (
          <div
            className="guest-suggestions"
            id={id + '-results'}
            role="listbox"
            aria-label={t('Suggestions d’invités')}
          >
            {choices.map((r, i) => (
              <button
                type="button"
                role="option"
                aria-selected={i === active}
                id={id + '-' + i}
                key={r.email}
                className={i === active ? 'highlighted' : ''}
                onMouseDown={(e) => e.preventDefault()}
                onMouseEnter={() => setActive(i)}
                onClick={() => add(r.email)}
              >
                <span className="guest-initial">{(r.name || r.email)[0].toUpperCase()}</span>
                <span>
                  <strong>{r.name || r.email}</strong>
                  <small>{r.email}</small>
                </span>
                <small>{sourceLabel(r.source)}</small>
              </button>
            ))}
            {!choices.length && (
              <p role="status">
                {loading
                  ? 'Recherche…'
                  : query
                    ? t('Aucune suggestion. Vous pouvez ajouter une adresse complète.')
                    : t('Saisissez un nom ou une adresse.')}
              </p>
            )}
          </div>
        )}
      </div>
      {error && (
        <p role="alert" className="guest-error">
          {error}
        </p>
      )}
      <p className="guest-help">
        {t(
          'Votre carnet, les adresses actives de votre domaine et vos correspondants. Entrée pour ajouter.',
        )}
      </p>
    </div>
  );
}
